/**
 * Gomoku Canvas Renderer
 * High-DPI responsive canvas, touch confirmation magnifier, visual effects
 */
import { BOARD_SIZE, EMPTY, PLAYER_BLACK, PLAYER_WHITE } from './engine.js';

export class GomokuRenderer {
  constructor(canvas, onMoveRequested) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.onMoveRequested = onMoveRequested;

    this.selectedCell = null; // For touch confirmation: { x, y }
    this.hoverCell = null;
    this.cellSize = 0;
    this.padding = 0;
    this.dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    this.boundHandlePointerDown = this.handlePointerDown.bind(this);
    this.canvas.addEventListener('pointerdown', this.boundHandlePointerDown);
  }

  resize() {
    const rect = this.canvas.parentElement.getBoundingClientRect();
    const size = Math.min(rect.width, rect.height || rect.width, 540);

    this.canvas.width = size * this.dpr;
    this.canvas.height = size * this.dpr;
    this.canvas.style.width = `${size}px`;
    this.canvas.style.height = `${size}px`;

    this.ctx.scale(this.dpr, this.dpr);

    this.padding = size * 0.06;
    this.cellSize = (size - this.padding * 2) / (BOARD_SIZE - 1);
  }

  handlePointerDown(e) {
    const rect = this.canvas.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;

    const x = Math.round((clientX - this.padding) / this.cellSize);
    const y = Math.round((clientY - this.padding) / this.cellSize);

    if (x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE) {
      if (this.selectedCell && this.selectedCell.x === x && this.selectedCell.y === y) {
        // Double tap / second tap on same cell confirms move!
        const target = { ...this.selectedCell };
        this.selectedCell = null;
        this.onMoveRequested(target.x, target.y);
      } else {
        // First tap: select / preview target
        this.selectedCell = { x, y };
        this.onMoveRequested(x, y, true); // true = preview only
      }
    }
  }

  confirmCurrentSelection() {
    if (this.selectedCell) {
      const { x, y } = this.selectedCell;
      this.selectedCell = null;
      this.onMoveRequested(x, y);
    }
  }

  clearSelection() {
    this.selectedCell = null;
  }

  render(engine, currentUserId, playerColor) {
    const size = this.canvas.width / this.dpr;
    const ctx = this.ctx;

    ctx.clearRect(0, 0, size, size);

    // 1. Board Background (Warm Modern Wood / Deep Arcade Slate)
    const boardBg = ctx.createLinearGradient(0, 0, size, size);
    boardBg.addColorStop(0, '#1c1917');
    boardBg.addColorStop(1, '#0f172a');
    ctx.fillStyle = boardBg;
    ctx.beginPath();
    ctx.roundRect(0, 0, size, size, 16);
    ctx.fill();

    // Board Border
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 2;
    ctx.stroke();

    // 2. Grid Lines
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 1;

    for (let i = 0; i < BOARD_SIZE; i++) {
      const pos = this.padding + i * this.cellSize;

      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(this.padding, pos);
      ctx.lineTo(size - this.padding, pos);
      ctx.stroke();

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(pos, this.padding);
      ctx.lineTo(pos, size - this.padding);
      ctx.stroke();
    }

    // 3. Star Points (Hoshi & Tengen)
    const starCoords = [3, 7, 11];
    ctx.fillStyle = '#64748b';
    for (const sx of starCoords) {
      for (const sy of starCoords) {
        const cx = this.padding + sx * this.cellSize;
        const cy = this.padding + sy * this.cellSize;
        ctx.beginPath();
        ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // 4. Stones
    const stoneRadius = this.cellSize * 0.44;

    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const val = engine.board[y][x];
        if (val !== EMPTY) {
          const cx = this.padding + x * this.cellSize;
          const cy = this.padding + y * this.cellSize;
          this.drawStone(cx, cy, val, stoneRadius);
        }
      }
    }

    // 5. Highlight Last Move
    if (engine.lastMove) {
      const cx = this.padding + engine.lastMove.x * this.cellSize;
      const cy = this.padding + engine.lastMove.y * this.cellSize;
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(cx, cy, stoneRadius * 0.5, 0, Math.PI * 2);
      ctx.stroke();
    }

    // 6. Preview / Touch Confirmation Magnifier
    if (this.selectedCell && engine.isValidMove(this.selectedCell.x, this.selectedCell.y)) {
      const cx = this.padding + this.selectedCell.x * this.cellSize;
      const cy = this.padding + this.selectedCell.y * this.cellSize;

      ctx.save();
      ctx.strokeStyle = '#06b6d4';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(cx, cy, stoneRadius * 1.15, 0, Math.PI * 2);
      ctx.stroke();

      // Semi-transparent phantom stone
      ctx.globalAlpha = 0.55;
      this.drawStone(cx, cy, playerColor, stoneRadius);
      ctx.restore();
    }

    // 7. Winning Sequence Line
    if (engine.winningLine && engine.winningLine.length > 0) {
      ctx.save();
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      const first = engine.winningLine[0];
      ctx.moveTo(this.padding + first.x * this.cellSize, this.padding + first.y * this.cellSize);
      for (let i = 1; i < engine.winningLine.length; i++) {
        const pt = engine.winningLine[i];
        ctx.lineTo(this.padding + pt.x * this.cellSize, this.padding + pt.y * this.cellSize);
      }
      ctx.stroke();

      // Halo glow around winning line
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.4)';
      ctx.lineWidth = 12;
      ctx.stroke();
      ctx.restore();
    }
  }

  drawStone(cx, cy, player, radius) {
    const ctx = this.ctx;
    ctx.save();

    // Drop Shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 3;

    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);

    if (player === PLAYER_BLACK) {
      const grad = ctx.createRadialGradient(cx - radius * 0.3, cy - radius * 0.3, radius * 0.1, cx, cy, radius);
      grad.addColorStop(0, '#475569');
      grad.addColorStop(0.4, '#1e293b');
      grad.addColorStop(1, '#020617');
      ctx.fillStyle = grad;
    } else {
      const grad = ctx.createRadialGradient(cx - radius * 0.3, cy - radius * 0.3, radius * 0.1, cx, cy, radius);
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(0.7, '#e2e8f0');
      grad.addColorStop(1, '#94a3b8');
      ctx.fillStyle = grad;
    }

    ctx.fill();
    ctx.restore();
  }

  destroy() {
    this.canvas.removeEventListener('pointerdown', this.boundHandlePointerDown);
  }
}
